/**
 * File Upload Style Module
 *
 * Styles for the reusable FileUpload component with drag-and-drop support.
 * Based on the photo upload design from BehaviorTracking.tsx.
 */

import type React from 'react';

/**
 * Base upload zone - dashed border container
 */
export const uploadZone: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: '10px',
  border: '2px dashed var(--border-color)',
  backgroundColor: 'var(--surface-color)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '0.75rem',
};

/**
 * Active drag-over state - highlighted border and background
 */
export const uploadZoneActive: React.CSSProperties = {
  ...uploadZone,
  borderColor: 'var(--primary-color)',
  backgroundColor: 'rgba(var(--primary-color-rgb, 59, 130, 246), 0.05)',
  transform: 'scale(1.01)',
};

/**
 * Disabled state - reduced opacity and no pointer
 */
export const uploadZoneDisabled: React.CSSProperties = {
  ...uploadZone,
  cursor: 'not-allowed',
  opacity: 0.6,
};

/**
 * Icon container styling
 */
export const uploadIcon: React.CSSProperties = {
  color: 'var(--text-secondary)',
  opacity: 0.7,
  flexShrink: 0,
};

/**
 * Text content container
 */
export const uploadTextContainer: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.125rem',
};

/**
 * Primary label text
 */
export const uploadLabel: React.CSSProperties = {
  fontWeight: '500',
  color: 'var(--text-primary)',
  fontSize: '0.875rem',
};

/**
 * Secondary hint text
 */
export const uploadHint: React.CSSProperties = {
  fontSize: '0.6875rem',
  color: 'var(--text-secondary)',
};

/**
 * Preview grid container
 */
export const previewGrid: React.CSSProperties = {
  display: 'grid',
  gap: '0.75rem',
  marginTop: '0.75rem',
};

/**
 * Individual preview item container
 */
export const previewItem: React.CSSProperties = {
  position: 'relative',
  aspectRatio: '1',
  borderRadius: 'var(--radius)',
  overflow: 'hidden',
  border: '2px solid var(--border-color)',
};

/**
 * Preview image styling
 */
export const previewImage: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

/**
 * Remove button on preview items
 */
export const removeButton: React.CSSProperties = {
  position: 'absolute',
  top: '0.25rem',
  right: '0.25rem',
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  border: 'none',
  backgroundColor: 'var(--error-color)',
  color: 'var(--on-primary-color)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'opacity 0.15s ease',
};

/**
 * Disabled remove button
 */
export const removeButtonDisabled: React.CSSProperties = {
  ...removeButton,
  cursor: 'not-allowed',
  opacity: 0.5,
};

// =============================================================================
// File List Styles (non-image file display: PDFs, docs, etc.)
// =============================================================================

/**
 * File list container — vertical list below the upload zone
 */
export const fileListContainer: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
  marginTop: '0.75rem',
};

/**
 * Individual file list item — compact row with icon, name, size, remove
 */
export const fileListItem: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.375rem 0.5rem',
  backgroundColor: 'var(--background-color)',
  borderRadius: 'var(--radius)',
  border: '1px solid var(--border-color)',
};

/**
 * File list icon styling
 */
export const fileListIcon: React.CSSProperties = {
  color: 'var(--text-tertiary)',
  flexShrink: 0,
};

/**
 * File list filename text
 */
export const fileListName: React.CSSProperties = {
  flex: 1,
  fontSize: '0.8rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/**
 * File list file size text
 */
export const fileListSize: React.CSSProperties = {
  fontSize: '0.7rem',
  color: 'var(--text-tertiary)',
  flexShrink: 0,
};

/**
 * File list remove button
 */
export const fileListRemoveButton: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-tertiary)',
  padding: '2px',
  borderRadius: 'var(--radius)',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/**
 * File list remove button — disabled state
 */
export const fileListRemoveButtonDisabled: React.CSSProperties = {
  ...fileListRemoveButton,
  cursor: 'not-allowed',
  opacity: 0.5,
};

// =============================================================================
// Large Variant Styles (for bulk imports, centered dropzones)
// =============================================================================

/**
 * Large upload zone - centered dropzone for bulk imports
 */
export const uploadZoneLarge: React.CSSProperties = {
  padding: '3rem',
  borderRadius: '10px',
  border: '2px dashed var(--border-color)',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: '0.5rem',
};

/**
 * Large variant active drag-over state
 */
export const uploadZoneLargeActive: React.CSSProperties = {
  ...uploadZoneLarge,
  borderColor: 'var(--primary-color)',
  backgroundColor: 'var(--hover-bg)',
};

/**
 * Large variant icon styling
 */
export const uploadIconLarge: React.CSSProperties = {
  color: 'var(--text-muted)',
  marginBottom: '0.5rem',
};

/**
 * Large variant label text
 */
export const uploadLabelLarge: React.CSSProperties = {
  fontWeight: '500',
  color: 'var(--text-primary)',
  fontSize: '1rem',
};

/**
 * Large variant hint text
 */
export const uploadHintLarge: React.CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--text-muted)',
};

/**
 * Export all styles as a single object for convenience
 */
export const fileUploadStyles = {
  // Compact variant (default)
  uploadZone,
  uploadZoneActive,
  uploadZoneDisabled,
  uploadIcon,
  uploadTextContainer,
  uploadLabel,
  uploadHint,
  // Large variant
  uploadZoneLarge,
  uploadZoneLargeActive,
  uploadIconLarge,
  uploadLabelLarge,
  uploadHintLarge,
  // Shared
  previewGrid,
  previewItem,
  previewImage,
  removeButton,
  removeButtonDisabled,
  // File list (non-image files)
  fileListContainer,
  fileListItem,
  fileListIcon,
  fileListName,
  fileListSize,
  fileListRemoveButton,
  fileListRemoveButtonDisabled,
};
